<template>
  <div>
    <v-slide-x-transition>
      <v-card
        v-if="open"
        class="notification"
        elevation="0"
        rounded="0"
        v-click-outside="closeModal"
        v-hotkey="keymap"
      >
        <!-- Notification Header Start -->
        <div
          class="notification-header d-flex align-center justify-space-between"
        >
          <h3 style="font-weight: 500">알림</h3>
          <div class="d-flex align-center">
            <span class="t-caption">안 읽은 메시지만 보기</span>
            <c-toggle
              class="ml-2"
              :value="toggle"
              @click="changeToggleState(toggle)"
            />
          </div>
        </div>
        <!-- Notification Header End -->

        <!-- Notification Content Start -->
        <div class="notification-content px-7 pb-5" ref="layout">
          <!-- Empty List Start -->
          <div
            v-if="!alarmList.length && !toggle"
            class="d-flex justify-center mt-5 t-caption tc-999"
          >
            받은 알림이 없습니다.
          </div>
          <div
            v-else-if="!alarmList.length && toggle"
            class="d-flex justify-center mt-5 t-caption tc-999"
          >
            모든 알림을 확인했습니다.
          </div>
          <!-- Empty List End -->

          <!-- List Content Start -->
          <div v-else>
            <div class="read-all t-caption tc-666" @click="changeReadAll">
              모두 읽음 표시
            </div>

            <div v-for="(alarmDay, i) in alarmList" :key="i">
              <div>
                <h3 v-if="alarmDay.titleDate === '0'" class="mt-5 mb-3 tc-333">
                  오늘
                </h3>
                <h3
                  v-else-if="alarmDay.titleDate === '1'"
                  class="mt-5 mb-3 tc-333"
                >
                  어제
                </h3>
                <h3
                  v-else-if="alarmDay.titleDate === '2'"
                  class="mt-5 mb-3 tc-333"
                >
                  이전
                </h3>
              </div>

              <div class="item-list">
                <div
                  v-for="(alarm, j) in alarmDay.alarmItems"
                  :key="j"
                  class="item"
                  @click="moveTo($event, alarm)"
                >
                  <!-- Type -->
                  <div class="type t-caption" :class="alarm.alarmType">
                    {{ getType(alarm.alarmType) }}
                  </div>
                  <!-- Type -->

                  <!-- Content -->
                  <div
                    class="content"
                    :class="{
                      disable:
                        alarm.alarmType !== 'S3_WORK_DOWNLOAD_SUCCESS' &&
                        !alarm.alarmMessage.projectId &&
                        !alarm.alarmMessage.taskId,
                    }"
                    v-html="getMsg(alarm.alarmType, alarm.alarmMessage)"
                  />
                  <!-- Content -->

                  <!-- Time -->
                  <div class="time t-caption tc-999">{{ alarm.alarmDate }}</div>
                  <!-- Time -->

                  <!-- Read Btn -->
                  <div class="read d-flex flex-row-reverse align-center">
                    <div
                      class="circle d-flex align-center justify-center"
                      @click.stop.prevent="
                        setReadOne(alarm.alarmCheck, alarm.alarmId)
                      "
                    >
                      <div v-if="!alarm.alarmCheck" class="inner-circle"></div>
                    </div>
                    <div class="hover-msg">
                      <span v-if="!alarm.alarmCheck">읽음으로 표시</span>
                      <span v-else>안 읽음으로 표시</span>
                    </div>
                  </div>
                  <!-- Read Btn -->
                </div>
              </div>
            </div>
          </div>
          <!-- List Content End -->
        </div>
        <!-- Notification Content End -->
      </v-card>
    </v-slide-x-transition>

    <div ref="toast" class="toast-wrapper d-flex justify-center">
      <t-toast
        v-model="toast"
        :time="800"
        width="194px"
        height="40px"
        class="align-center"
      >
        <div class="d-flex justify-space-between align-center px-5">
          <img src="/img/dataset/icon_clipboard.svg" alt="icon-copy" />
          <span>클립보드에 복사되었습니다.</span>
        </div>
      </t-toast>
    </div>
  </div>
</template>

<script>
import { iAlarm, iDashBoard } from "@/services";

import util from "@/common/util";
import Toggle from "@/components/CToggle.vue";
import TreeDToast from "@/components/TreeDToast";

export default {
  components: {
    "c-toggle": Toggle,
    "t-toast": TreeDToast,
  },
  props: {
    open: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => {
    return {
      options: {
        type: "ALL",
        page: 1,
        limit: 20,
      },
      alarmList: [],
      alarmTotal: 0,

      uploadId: "",
      toast: false,
    };
  },
  computed: {
    keymap() {
      return {
        escape: {
          keydown: () => {
            if (!this.open) {
              return;
            }
            this.closeModal();
          },
        },
      };
    },
    toggle() {
      return this.$store.state.alarm.toggle;
    },
    badgeCount() {
      return this.$store.state.alarm.badgeCount;
    },
    panelTreeProjects() {
      return this.$store.state.dashboard.panelTreeProjects;
    },
  },
  watch: {
    async open() {
      if (this.open) {
        if (!this.toggle) {
          this.options.type = "ALL";
        } else {
          this.options.type = "UN_READ";
        }
        await this.setAlarmList();
        this.setInfiniteScroll();
      } else {
        this.clearData();
      }
    },
    badgeCount() {
      if (this.open) {
        this.setAlarmListPage();
      }
    },
  },
  methods: {
    // 알림 무한스크롤 구현
    setInfiniteScroll() {
      this.$refs.layout.addEventListener("scroll", () => {
        let clientH = this.$refs.layout.clientHeight;
        let scrollH = this.$refs.layout.scrollHeight;
        let scrollT = this.$refs.layout.scrollTop;
        let scrollB = clientH + scrollT;

        if (
          scrollH - 10 < scrollB &&
          Math.floor(this.alarmTotal / this.options.limit) >= this.options.page
        ) {
          this.options.page++;
          this.addAlarmList();
        }
      });
    },
    // 알림 list 가져오기
    async setAlarmList() {
      const result = await iAlarm.getAlarmList(this.options);
      this.alarmList = result.data.alarms;
      this.alarmTotal = result.data.totalCount;
    },
    // 스크롤 - 알림 list 추가
    async addAlarmList() {
      const result = await iAlarm.getAlarmList(this.options);

      result.data.alarms.forEach((obj) => {
        let alarmObj = this.alarmList.find((a) => {
          return a.titleDate === obj.titleDate;
        });

        if (alarmObj) {
          alarmObj.alarmItems.push.apply(alarmObj.alarmItems, obj.alarmItems);
        }
      });
    },
    // 현재 페이지 만큼 알림 list 가져오기
    async setAlarmListPage() {
      const result = await iAlarm.getAlarmList({
        type: this.options.type,
        page: 0,
        limit: this.options.limit * this.options.page,
      });
      this.alarmList = result.data.alarms;
    },

    // 모달 닫기
    closeModal() {
      this.$emit("update:open", false);
    },
    // 값 초기화
    clearData() {
      this.alarmList = [];
      this.options = {
        type: "ALL",
        page: 1,
        limit: 20,
      };
    },
    // 메시지 타입
    getType(type) {
      switch (type) {
        case "PROJECT_INVITE":
          return "프로젝트 할당";
        case "PROJECT_DELETE":
          return "프로젝트 삭제";
        case "TASK_INVITE_OPERATOR":
          return "작업 할당 (작업자)";
        case "TASK_DELETE_OPERATOR":
          return "작업 삭제 (작업자)";
        case "TASK_INVITE_INSPECTOR":
          return "작업 할당 (검수자)";
        case "TASK_DELETE_INSPECTOR":
          return "작업 삭제 (검수자)";
        case "TASK_INSPECT_COMPLETE":
          return "검수 완료";
        case "WORK_REJECT":
          return "검수 반려";
        case "WORK_REJECT_COMPLETE":
          return "재작업 완료";
        case "WORK_COMMENT":
          return "작업물 코멘트";
        case "TASK_UNASSIGNED_WORKER":
          return "미할당 작업";
        case "DATASET_UPLOAD_SUCCESS":
          return "데이터셋 업로드 완료";
        case "DATASET_SYNC_SUCCESS":
          return "데이터셋 동기화 완료";
        case "DATASET_UPLOAD_FAIL":
          return "데이터셋 업로드 미완료";
        case "DATASET_SYNC_FAIL":
          return "데이터셋 동기화 미완료";
        case "S3_WORK_DOWNLOAD_SUCCESS":
          return "작업 다운로드 준비 완료";
        case "WORK_DOWNLOAD_FAIL":
          return "작업 다운로드 실패";
        case "PROJECT_COMPLETE_MANAGER":
          return "프로젝트 완료";
        case "PROJECT_COMPLETE_MANAGER_AND_SUPERVISOR":
          return "프로젝트 완료";
        case "PROJECT_COMPLETE_INSPECTOR_AND_OPERATOR":
          return "프로젝트 완료";
        case "PROJECT_TARGET_WORKLOAD_SATISFED":
          return "목표 작업량 충족";
        case "ASSIGN_TEST":
          return "테스트 파일 할당";
        case "PASS_TEST":
          return "테스트 합격";
        case "FAIL_TEST":
          return "테스트 불합격";
        case "TEST_DELETED":
          return "작업 삭제";
        case "TEST_SUBMITTED":
          return "테스트 응시 완료";
      }
    },
    // 메시지 내용
    getMsg(type, msg) {
      switch (type) {
        case "PROJECT_INVITE":
          return `<span>[${msg.projectName}]</span> 프로젝트에 배정되었습니다.`;
        case "PROJECT_DELETE":
          return `<span>${msg.worker}(${msg.role})</span>님이 <span>[${msg.projectName}]</span> 프로젝트를 삭제했습니다.`;
        case "TASK_INVITE_OPERATOR":
          return `<span>[${msg.taskName}]</span> 작업의 작업자로 배정되었습니다.`;
        case "TASK_DELETE_OPERATOR":
          return `작업자로 배정됐던 <span>[${msg.taskName}]</span> 작업을 <span>${msg.worker}(${msg.role})</span>님이 삭제했습니다.`;
        case "TASK_INVITE_INSPECTOR":
          return `<span>[${msg.taskName}]</span> 작업의 검수자로 배정되었습니다.`;
        case "TASK_DELETE_INSPECTOR":
          return `검수자로 배정됐던 <span>[${msg.taskName}]</span> 작업을 <span>${msg.worker}(${msg.role})</span>님이 삭제했습니다.`;
        case "TASK_INSPECT_COMPLETE":
          return `<span>[${msg.taskName}]</span> 작업의 검수가 끝났습니다.`;
        case "WORK_REJECT":
          return `<span>[${msg.taskName}]</span> 작업에 반려된 작업물이 있습니다.`;
        case "WORK_REJECT_COMPLETE":
          return `<span>[${msg.taskName}]</span> 작업에 재작업된 작업물이 있습니다.`;
        case "WORK_COMMENT":
          return `<span>[${msg.taskName}]</span> 작업물에 <span>${msg.worker}(${msg.role})</span>님이 코멘트를 남겼습니다.`;
        case "TASK_UNASSIGNED_WORKER":
          return `<span>[${msg.taskName}]</span> 작업에 아직 작업자가 없습니다.`;
        case "DATASET_UPLOAD_SUCCESS":
          return `<span>[${msg.projectName}]</span> 프로젝트의 데이터셋 업로드가 완료되었습니다.`;
        case "DATASET_SYNC_SUCCESS":
          return `<span>[${msg.projectName}]</span> 프로젝트의 데이터셋 동기화가 완료되었습니다.`;
        case "DATASET_UPLOAD_FAIL":
          return `<span>[${msg.projectName}]</span> 프로젝트의 데이터셋 업로드를 마쳤으나 정상적으로 업로드되지 않은 파일이 있습니다.`;
        case "DATASET_SYNC_FAIL":
          return `<span>[${msg.projectName}]</span> 프로젝트의 데이터셋 동기화를 마쳤으나 정상적으로 동기화되지 않은 파일이 있습니다.`;
        case "S3_WORK_DOWNLOAD_SUCCESS":
          return `<span>[${msg.projectName || msg.taskName}]</span>
          의 작업물을 다운로드할 수 있습니다. 파일을 저장하려면 이 메시지를 클릭하세요.`;
        case "WORK_DOWNLOAD_FAIL":
          return `<span>[${msg.projectName || msg.taskName}]</span>
          의 작업물을 다운로드 할 수 없습니다. 잠시 후 다시 시도해 주세요.`;
        case "PROJECT_COMPLETE_MANAGER_AND_SUPERVISOR":
          return `<span>[${msg.projectName}]</span> 프로젝트가 완료되었습니다. 이 프로젝트의 모든 데이터는 3개월 후 자동으로 삭제됩니다.`;
        case "PROJECT_COMPLETE_INSPECTOR_AND_OPERATOR":
          return `<span>[${msg.projectName}]</span> 프로젝트가 완료되었습니다. 작업 목록은 확인할 수 있지만 작업 공간에는 들어갈 수 없습니다.`;
        case "PROJECT_TARGET_WORKLOAD_SATISFED":
          return `<span>[${msg.projectName}]</span> 프로젝트의 목표 작업량이 충족되었습니다. 작업 수정 및 검수만 계속 진행할 수 있습니다.`;
        case "ASSIGN_TEST":
          return `<span>[${msg.taskName}]</span> 작업 참여를 위한 테스트 파일이 할당되었습니다. 테스트에 응시 완료해 주세요.`;
        case "PASS_TEST":
          return `<span>[${msg.taskName}]</span> 작업의 ${
            msg.testType === "O" ? "작업자" : "검수자"
          } 테스트에 통과했습니다. 곧 전자 계약서를 보내드릴 예정이며, 관리자가 계약 서명을 확인하면 실작업에 참여할 수 있습니다.`;
        case "FAIL_TEST":
          return `<span>[${msg.taskName}]</span> 작업의 ${
            msg.testType === "O" ? "작업자" : "검수자"
          } 테스트에 통과하지 못하여 작업에 참여할 수 없습니다.`;
        case "TEST_DELETED":
          return `테스트 중이었던 <span>[${msg.taskName}]</span> 작업을 ${msg.worker}(${msg.role})님이 삭제했습니다.`;
        case "TEST_SUBMITTED":
          return `<span>[${msg.taskName}]</span> 작업의 ${
            msg.testType === "O" ? "작업자" : "검수자"
          } 테스트에 응시 완료한 사용자가 있습니다 (${msg.worker}).`;
      }
    },
    // 안 읽은 메시지만 보기 토글
    changeToggleState(toggle) {
      if (toggle) {
        this.options.type = "ALL";
      } else {
        this.options.type = "UN_READ";
      }

      this.$store.commit("alarm/setToggleState", !toggle);

      this.alarmList = [];
      this.setAlarmListPage();
    },
    // 모두 읽음 표시
    async changeReadAll() {
      await this.$store.dispatch("alarm/setAlarmReadAll");
      this.setAlarmListPage();
    },
    // 읽음으로 표시 / 읽지 않음으로 표시
    async setReadOne(read, id) {
      if (read) {
        // 읽지 않음으로 표시
        await this.$store.dispatch("alarm/setAlarmUnReadOne", { alarmId: id });
      } else {
        // 읽음으로 표시
        await this.$store.dispatch("alarm/setAlarmReadOne", { alarmId: id });
      }

      this.setAlarmListPage();
    },
    // 알림 클릭 이동
    async moveTo(e, alarm) {
      if (!alarm.alarmCheck) {
        this.setReadOne(alarm.alarmCheck, alarm.alarmId);
      }

      const classNames = Array.from(e.target.classList);
      if (classNames.includes("action")) {
        if (classNames.includes("copy-text")) {
          util.copyClipboard(e.target.innerText);
          this.toast = true;

          this.$refs.toast.style.left = `${e.clientX + 20}px`;
          this.$refs.toast.style.top = `${e.clientY + 20}px`;
        }
        return;
      }

      switch (alarm.alarmType) {
        case "PROJECT_INVITE":
        case "PROJECT_COMPLETE_MANAGER_AND_SUPERVISOR":
        case "PROJECT_COMPLETE_INSPECTOR_AND_OPERATOR":
        case "PROJECT_TARGET_WORKLOAD_SATISFED": {
          this.alarmTargetRouter(alarm, "parent", "dashboard/summary");
          break;
        }

        case "TASK_INVITE_OPERATOR":
        case "TASK_INVITE_INSPECTOR":
        case "TASK_INSPECT_COMPLETE": {
          this.alarmTargetRouter(alarm, "children", "dashboard/status");
          break;
        }
        case "S3_WORK_DOWNLOAD_SUCCESS": {
          location.href = window.BASE_URL + alarm.alarmMessage.downloadUrl;
          break;
        }
        case "DATASET_UPLOAD_SUCCESS":
        case "DATASET_UPLOAD_FAIL": {
          const payload = {
            currentId: alarm.projectId,
            currentType: "project",
            projectId: alarm.projectId,
            isAdmin: true,
          };
          this.$store.dispatch("dashboard/setCurrent", payload);
          this.$store.commit("history/setProjectId", payload.projectId);
          this.alarmTargetRouter(alarm, "parent", "dataset");
          break;
        }
        case "DATASET_SYNC_SUCCESS":
        case "DATASET_SYNC_FAIL": {
          const payload = {
            currentId: alarm.projectId,
            currentType: "project",
            projectId: alarm.projectId,
            isAdmin: true,
          };
          this.$store.dispatch("dashboard/setCurrent", payload);
          // 프로젝트 아이디를 히스토리 스토어에 저장
          this.$store.commit("history/setProjectId", payload.projectId);
          this.alarmTargetRouter(alarm, "parent", "dataset");
          break;
        }
        case "TASK_DELETE_OPERATOR":
        case "TASK_DELETE_INSPECTOR":
        case "PROJECT_DELETE":
        case "FAIL_TEST":
        case "TEST_DELETED": {
          if (!alarm.alarmCheck) {
            this.$store.dispatch("alarm/setAlarmReadOne", {
              alarmId: alarm.alarmId,
            });
          }
          break;
        }
        case "ASSIGN_TEST":
        case "TEST_SUBMITTED":
        case "PASS_TEST": {
          this.alarmTargetRouter(alarm, "children", "dashboard/test");
          break;
        }
        default: {
          this.$router.push(alarm.alarmTargetPath);
        }
      }

      this.closeModal();
    },
    /**
     * @param {object} alarm // 알람 타켓
     * @param {string} type  // parent, children 구분
     * @param {string} path  // 페이지 경로
     */
    async alarmTargetRouter(alarm, type, path) {
      const parentData = this.panelTreeProjects.find(
        (item) => item.projectId === alarm.projectId
      );
      if (!parentData) return;

      let targetData = parentData;
      if (type === "children") {
        const res = await iDashBoard.getDashboardTasks(alarm.projectId);
        const taskList = res.data.payload;
        targetData = taskList.find((item) => item.taskId === alarm.taskId);
        targetData.projectId = alarm.projectId;
      }

      if (targetData) {
        const url = util.getDashboardUrl(
          path,
          targetData.projectId,
          targetData.taskId
        );
        window.location.href = url;
      }
    },
  },
};
</script>

<style scoped>
.notification {
  width: 460px;
  position: fixed;
  right: 30px;
  top: 50px;
  bottom: 36px;
  background-color: var(--c-white);
  border-radius: 5px 5px 0px 0px !important;
  box-shadow: -4px 4px 20px rgba(0, 0, 0, 0.1) !important;
}

.notification .notification-header {
  padding: 0 30px;
  width: 100%;
  height: 60px;
  background-color: #0085cf;
  color: var(--c-white);
  border-radius: 0px !important;
}

.notification .notification-content {
  position: relative;
  height: calc(100% - 60px);
  overflow-y: auto;
}

.notification .notification-content .read-all {
  position: absolute;
  top: 24px;
  right: 30px;
  cursor: pointer;
}

.notification .notification-content .read-all:hover {
  text-decoration: underline;
}

.notification .notification-content .item-list .item {
  position: relative;
  padding: 16px;
  width: 100%;
  border-radius: 5px;
  cursor: pointer;
  transition: ease-in-out 0.1s;
}

.notification .notification-content .item-list .item:hover {
  background-color: #f5f5f5;
}

.notification
  .notification-content
  .item-list
  .item:hover
  .content:not(.disable) {
  text-decoration: underline;
}

.notification .notification-content .item-list .item .type {
  font-weight: 500;
}

.item-list .item .type.DATASET_DELETE,
.item-list .item .type.TASK_DELETE_OPERATOR,
.item-list .item .type.TASK_DELETE_INSPECTOR {
  color: #333333;
}

.item-list .item .type.WORK_REJECT,
.item-list .item .type.TASK_UNASSIGNED_WORKER,
.item-list .item .type.WORK_DOWNLOAD_FAIL,
.item-list .item .type.FAIL_TEST {
  color: #c83c50;
}

.item-list .item .type.PROJECT_INVITE,
.item-list .item .type.TASK_INVITE_OPERATOR,
.item-list .item .type.TASK_INVITE_INSPECTOR,
.item-list .item .ASSIGN_TEST,
.item-list .item .TEST_SUBMITTED {
  color: #0085cf;
}

.item-list .item .type.TASK_INSPECT_COMPLETE,
.item-list .item .type.WORK_REJECT_COMPLETE,
.item-list .item .type.DATASET_UPLOAD_SUCCESS,
.item-list .item .type.DATASET_SYNC_SUCCESS,
.item-list .item .type.S3_WORK_DOWNLOAD_SUCCESS,
.item-list .item .PROJECT_COMPLETE_MANAGER_AND_SUPERVISOR,
.item-list .item .PROJECT_COMPLETE_INSPECTOR_AND_OPERATOR,
.item-list .item .PROJECT_TARGET_WORKLOAD_SATISFED,
.item-list .item .PASS_TEST {
  color: #1eaa82;
}

.item-list .item .type.WORK_COMMENT,
.item-list .item .type.DATASET_UPLOAD_FAIL,
.item-list .item .type.DATASET_SYNC_FAIL {
  color: #f0a050;
}

.notification .notification-content .item-list .item .content {
  color: #666;
  display: inline-block;
  margin: 5px 0;
}

.notification .notification-content .item-list .item .read {
  position: absolute;
  top: 14px;
  right: 11px;
}

.notification .notification-content .item-list .item .read .circle {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid transparent;
  transition: ease-in-out 0.3s;
}

.notification .notification-content .item-list .item .read .circle:hover,
.notification .notification-content .item-list .item:hover .circle {
  border: 1px solid var(--c-999);
}

.notification
  .notification-content
  .item-list
  .item
  .read
  .circle
  .inner-circle {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #0085cf;
}

.notification
  .notification-content
  .item-list
  .item
  .read
  .circle:hover
  + .hover-msg {
  visibility: visible;
  opacity: 80%;
}

.notification .notification-content .item-list .item .read .hover-msg {
  visibility: hidden;
  opacity: 0;

  margin-right: 9px;
  padding: 3px 6px;
  line-height: 100%;
  border-radius: 2px;
  background-color: var(--c-333);
  display: flex;
  align-items: center;
  transition: ease-in-out 0.3s;
}

.notification .notification-content .item-list .item .read .hover-msg span {
  font-size: 10px;
  font-weight: 400;
  color: var(--c-white);
}

.toast-wrapper {
  position: fixed;
}

.toast-wrapper span {
  margin-left: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #333;
}
</style>

<style>
.notification .notification-content .item-list .item .content span {
  color: #333 !important;
  font-weight: 500 !important;
}

.notification
  .notification-content
  .item-list
  .item
  .content
  span.copy-text:hover {
  cursor: pointer;
  text-decoration: underline;
}
</style>
